import { WithDisposable } from '@blocksuite/global/lit';
import { batch } from '@preact/signals-core';
import { css, html, type PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';

import {
  type EditorHost,
  isGfxBlockComponent,
  ShadowlessElement,
} from '../view';
import { PropTypes, requiredProperties } from '../view/decorators/required';
import { GfxControllerIdentifier } from './identifiers';
import { GfxBlockElementModel } from './model/gfx-block-model';
import { measureGfxPerformance } from './performance';
import { Viewport } from './viewport';

type PendingGfxUpdate = {
  id: string;
  update?: () => void | Promise<unknown>;
  resolve: () => void;
  reject: (reason: unknown) => void;
  done: Promise<void>;
};

function sameIds(a: ReadonlySet<string>, b: ReadonlySet<string>) {
  if (a.size !== b.size) return false;
  for (const id of a) if (!b.has(id)) return false;
  return true;
}

/**
 * The blocks the canvas blocks in sight show: their whole subtrees, whose
 * entries have no canvas place of their own.
 */
export function contentBlocksInSight(
  models: Iterable<{ id: string }>,
  children: ReadonlyMap<string, readonly string[]>
): string[] {
  const ids: string[] = [];
  const pending = [...models].map(model => model.id);
  for (let id = pending.pop(); id !== undefined; id = pending.pop()) {
    for (const child of children.get(id) ?? []) {
      ids.push(child);
      pending.push(child);
    }
  }
  return ids;
}

/**
 * A wrapper around `requestConnectedFrame` that only calls at most once in one frame
 */
export function requestThrottledConnectedFrame<
  T extends (...args: unknown[]) => void,
>(func: T, element?: HTMLElement): T {
  let raqId: number | undefined = undefined;
  let latestArgs: unknown[] = [];

  return ((...args: unknown[]) => {
    latestArgs = args;

    if (raqId === undefined) {
      raqId = requestAnimationFrame(() => {
        raqId = undefined;

        if (!element || element.isConnected) {
          func(...latestArgs);
        }
      });
    }
  }) as T;
}

@requiredProperties({
  viewport: PropTypes.instanceOf(Viewport),
})
export class GfxViewportElement extends WithDisposable(ShadowlessElement) {
  static override styles = css`
    gfx-viewport {
      position: absolute;
      left: 0;
      top: 0;
      contain: size layout style;
      display: block;
      transform: none;
    }

    /* CSS for idle blocks that are hidden but maintain layout */
    .block-idle {
      visibility: hidden;
      content-visibility: hidden;
      pointer-events: none;
      contain: size layout style;
    }

    /* CSS for active blocks participating in viewport transformations */
    .block-active {
      visibility: visible;
      content-visibility: visible;
      pointer-events: auto;
      /*
       * Every DOM-backed block has an explicit canvas rect. Keep its live DOM
       * subtree behind the same containment boundary used while it is idle so
       * a viewport transform only invalidates the block owner, not thousands
       * of database descendants.
       */
      contain: size layout style;
    }
  `;

  private readonly _hideOutsideAndNoSelectedBlock = () => {
    if (!this.host) return;

    measureGfxPerformance(
      this.host.std,
      'viewport-cull',
      () => this._updateVisibleModels(),
      () => this._lastVisibleModels?.size ?? 0
    );
  };

  private readonly _updateVisibleModels = () => {
    if (!this.host) return;

    const gfx = this.host.std.get(GfxControllerIdentifier);
    const currentViewportModels = this.getModelsInViewport();
    const currentSelectedModels = this._getSelectedModels();
    const shouldBeVisible = new Set([
      ...currentViewportModels,
      ...currentSelectedModels,
    ]);

    const previousVisible = this._lastVisibleModels
      ? new Set(this._lastVisibleModels)
      : new Set<GfxBlockElementModel>();

    batch(() => {
      // Step 1: Activate all the blocks that should be visible
      shouldBeVisible.forEach(model => {
        const view = gfx.view.get(model);
        if (!isGfxBlockComponent(view)) return;
        view.transformState$.value = 'active';
      });

      // Step 2: Hide all the blocks that should not be visible
      previousVisible.forEach(model => {
        if (shouldBeVisible.has(model)) return;

        const view = gfx.view.get(model);
        if (!isGfxBlockComponent(view)) return;
        view.transformState$.value = 'idle';
      });
    });

    this._lastVisibleModels = shouldBeVisible;

    const { x, y, w, h } = this.viewport.viewportBounds;
    const { pageStructure } = this.host.store;
    pageStructure.setVisibleRect([x, y, w, h]);
    // Note contents have no canvas place: their parts load with the note.
    pageStructure.setVisibleBlocks(
      contentBlocksInSight(
        currentViewportModels,
        pageStructure.tree().children
      ),
      [...currentViewportModels]
        .filter(model => pageStructure.entry(model.id)?.k === 'affine:note')
        .map(model => model.id),
      []
    );
  };

  private _lastVisibleModels?: Set<GfxBlockElementModel>;

  private readonly _pendingChildrenUpdates = new Set<PendingGfxUpdate>();

  private _childrenFrame: number | undefined;

  private _childrenGeneration = 0;

  private _childrenPriorityDirty = false;

  private _childrenIterator: IterableIterator<PendingGfxUpdate> | undefined;

  private _queuedVisibleIds = new Set<string>();

  private _queuedFieldIds = new Set<string>();

  /** Blocks outside the focus field: they render once the viewport nears. */
  private readonly _waitingChildrenUpdates = new Set<PendingGfxUpdate>();

  private readonly _forcedIds = new Set<string>();

  private readonly _refreshViewport = requestThrottledConnectedFrame(() => {
    this._hideOutsideAndNoSelectedBlock();
    // The focus field moved: waiting blocks it reaches now render.
    if (this._waitingChildrenUpdates.size) this._scheduleChildren();
  }, this);

  private _updatingChildrenFlag = false;

  override connectedCallback(): void {
    super.connectedCallback();

    const viewportUpdateCallback = () => {
      this._refreshViewport();
    };

    if (!this.enableChildrenSchedule) {
      delete this.scheduleUpdateChildren;
    }

    if (this.host) {
      // Selected canvas elements keep their parts while out of sight.
      const gfx = this.host.std.get(GfxControllerIdentifier);
      const structure = this.host.store.pageStructure;
      this.disposables.add(
        structure.addUnloadGuard(partId =>
          gfx.selection.selectedIds.some(
            id => structure.entry(id)?.s === partId
          )
        )
      );
    }
    this._hideOutsideAndNoSelectedBlock();
    this.disposables.add(
      this.viewport.viewportUpdated.subscribe(() => viewportUpdateCallback())
    );
    this.disposables.add(
      this.viewport.sizeUpdated.subscribe(() => viewportUpdateCallback())
    );
  }

  override render() {
    return html``;
  }

  override updated(changed: PropertyValues<this>): void {
    super.updated(changed);
    // A changed focus field admits or holds back the scheduled blocks.
    if (
      (changed.has('focusFieldEnabled') ||
        changed.has('focusMarginViewports')) &&
      this._waitingChildrenUpdates.size + this._pendingChildrenUpdates.size
    )
      this._scheduleChildren();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._childrenGeneration++;
    if (this._childrenFrame !== undefined) {
      cancelAnimationFrame(this._childrenFrame);
      this._childrenFrame = undefined;
    }
    // Settle Lit's pending updates even if navigation detached the viewport.
    // Resource owners independently discard results from disposed lifetimes.
    const pending = [
      ...this._pendingChildrenUpdates,
      ...this._waitingChildrenUpdates,
    ];
    this._pendingChildrenUpdates.clear();
    this._waitingChildrenUpdates.clear();
    this._forcedIds.clear();
    this._childrenIterator = undefined;
    this._queuedVisibleIds.clear();
    this._queuedFieldIds.clear();
    pending.forEach(child => {
      void this._updateChild(child);
    });
    this._updatingChildrenFlag = false;
  }

  private async _updateChild(child: PendingGfxUpdate) {
    try {
      if (this.host && this.isConnected) {
        await measureGfxPerformance(
          this.host.std,
          'viewport-initial-render',
          () => child.update?.(),
          () => 1
        );
      } else {
        await child.update?.();
      }
      child.resolve();
    } catch (error) {
      child.reject(error);
    }
  }

  private readonly _flushChildren = async () => {
    this._childrenFrame = undefined;
    const generation = this._childrenGeneration;
    if (!this.isConnected) return;
    const started = performance.now();
    const visibleIds = new Set(
      [...this.getModelsInViewport(), ...this._getSelectedModels()].map(
        model => model.id
      )
    );
    const fieldIds = this._focusFieldIds();
    // Stable partition: keep model/DOM order within each priority. This only
    // changes admission order, never ownership, persisted geometry or layout.
    // Sight first, then the rest of the focus field (inside out); a block
    // outside the field waits until the viewport reaches it. Repartition only
    // on new arrivals or a changed viewport/selection, and remove Set entries
    // in O(1) instead of shifting a large queue each update.
    if (
      this._childrenPriorityDirty ||
      !sameIds(visibleIds, this._queuedVisibleIds) ||
      !sameIds(fieldIds, this._queuedFieldIds)
    ) {
      const visible: PendingGfxUpdate[] = [];
      const field: PendingGfxUpdate[] = [];
      const children = [
        ...this._pendingChildrenUpdates,
        ...this._waitingChildrenUpdates,
      ];
      this._pendingChildrenUpdates.clear();
      this._waitingChildrenUpdates.clear();
      children.forEach(child => {
        if (visibleIds.has(child.id)) visible.push(child);
        else if (fieldIds.has(child.id)) field.push(child);
        else this._waitingChildrenUpdates.add(child);
      });
      visible.forEach(child => this._pendingChildrenUpdates.add(child));
      field.forEach(child => this._pendingChildrenUpdates.add(child));
      this._childrenIterator = undefined;
      this._queuedVisibleIds = visibleIds;
      this._queuedFieldIds = fieldIds;
      this._childrenPriorityDirty = false;
    }

    let rendered = 0;
    const queue = (this._childrenIterator ??=
      this._pendingChildrenUpdates.values());
    const limit = Math.max(1, this.maxConcurrentRenders);
    while (
      this.isConnected &&
      generation === this._childrenGeneration &&
      this._pendingChildrenUpdates.size &&
      rendered < limit
    ) {
      if (
        rendered > 0 &&
        this.initialRenderBudgetMs > 0 &&
        performance.now() - started >= this.initialRenderBudgetMs
      ) {
        break;
      }
      const child = queue.next().value!;
      this._pendingChildrenUpdates.delete(child);
      await this._updateChild(child);
      rendered++;
    }

    if (generation !== this._childrenGeneration) return;
    if (this.isConnected && this._pendingChildrenUpdates.size) {
      this._childrenFrame = requestAnimationFrame(
        () => void this._flushChildren()
      );
    } else {
      this._updatingChildrenFlag = false;
    }
  };

  scheduleUpdateChildren? = (
    id: string,
    update?: () => void | Promise<unknown>
  ) => {
    const { promise, resolve, reject } = Promise.withResolvers<void>();

    const child = { id, update, resolve, reject, done: promise };
    if (!this.isConnected) {
      void this._updateChild(child);
      return promise;
    }
    this._pendingChildrenUpdates.add(child);
    this._childrenPriorityDirty = true;
    this._scheduleChildren();

    return promise;
  };

  /**
   * Renders the blocks `ids` wherever they are, as a reader of their views
   * (an export) needs them; resolves once each has rendered.
   */
  async renderBlocks(ids: Iterable<string>): Promise<void> {
    const wanted = new Set(ids);
    const children = [
      ...this._pendingChildrenUpdates,
      ...this._waitingChildrenUpdates,
    ].filter(child => wanted.has(child.id));
    if (!children.length) return;
    children.forEach(child => this._forcedIds.add(child.id));
    this._childrenPriorityDirty = true;
    this._scheduleChildren();
    try {
      await Promise.all(children.map(child => child.done));
    } finally {
      children.forEach(child => this._forcedIds.delete(child.id));
    }
  }

  private _scheduleChildren() {
    if (this._updatingChildrenFlag) return;
    this._updatingChildrenFlag = true;
    this._childrenFrame = requestAnimationFrame(
      () => void this._flushChildren()
    );
  }

  /** The viewport widened by `focusMarginViewports` on each side, selected
   * blocks and blocks a reader asked for. */
  private _focusFieldIds(): Set<string> {
    // Without a focus field every scheduled block is in it.
    if (!this.focusFieldEnabled)
      return new Set(
        [...this._pendingChildrenUpdates, ...this._waitingChildrenUpdates].map(
          child => child.id
        )
      );
    const { x, y, w, h } = this.viewport.viewportBounds;
    const margin = this.focusMarginViewports;
    const ids = new Set(this._forcedIds);
    for (const model of this.getModelsInBound({
      x: x - w * margin,
      y: y - h * margin,
      w: w * (1 + 2 * margin),
      h: h * (1 + 2 * margin),
    }))
      ids.add(model.id);
    for (const model of this._getSelectedModels()) ids.add(model.id);
    return ids;
  }

  private _getSelectedModels(): Set<GfxBlockElementModel> {
    if (!this.host) return new Set();
    const gfx = this.host.std.get(GfxControllerIdentifier);
    return new Set(
      gfx.selection.surfaceSelections
        .flatMap(({ elements }) => elements)
        .map(id => gfx.getElementById(id))
        .filter(e => e instanceof GfxBlockElementModel)
    );
  }

  /** The blocks in the viewport. */
  getModelsInViewport(): Set<GfxBlockElementModel> {
    return this.getModelsInBound(this.viewport.viewportBounds);
  }

  @property({ attribute: false })
  accessor host: undefined | EditorHost;

  /** The blocks in `bound`: the viewport and the focus field read them. */
  @property({ attribute: false })
  accessor getModelsInBound: (bound: {
    x: number;
    y: number;
    w: number;
    h: number;
  }) => Set<GfxBlockElementModel> = () => new Set();

  /** The focus field: the viewport widened by this many viewports on each
   * side. A block outside it renders once the viewport nears (setting
   * `edgelessFocusMargin`). */
  @property({ type: Number })
  accessor focusMarginViewports: number = 1;

  /** Off: every block renders, wherever it is (setting `edgelessFocusField`). */
  @property({ attribute: false })
  accessor focusFieldEnabled: boolean = true;

  @property({ type: Number })
  accessor maxConcurrentRenders: number = 2;

  /** CPU admission budget; a single expensive host is never split mid-update. */
  @property({ type: Number })
  accessor initialRenderBudgetMs: number = 0;

  @property({ attribute: false })
  accessor enableChildrenSchedule: boolean = true;

  @property({ attribute: false })
  accessor viewport!: Viewport;

  setBlocksActive(blockIds: string[]): void {
    if (!this.host) return;
    const gfx = this.host.std.get(GfxControllerIdentifier);

    batch(() => {
      blockIds.forEach(id => {
        const view = gfx.view.get(id);
        if (isGfxBlockComponent(view)) {
          view.transformState$.value = 'active';
        }
      });
    });
  }

  setBlocksIdle(blockIds: string[]): void {
    if (!this.host) return;
    const gfx = this.host.std.get(GfxControllerIdentifier);

    batch(() => {
      blockIds.forEach(id => {
        const view = gfx.view.get(id);
        if (isGfxBlockComponent(view)) {
          view.transformState$.value = 'idle';
        }
      });
    });
  }
}
